<template>
  <b-modal
    id="expiration-modal"
    centered
    :hide-header-close="submitting"
    :no-close-on-backdrop="submitting"
    :no-close-on-esc="submitting"
    :title="MODAL_TITLE"
    @hide="onHide"
    @show="onShow"
  >
    <MyLoading :loading-show="loadingShow" />
    <p class="font-weight-bold mb-1">
      {{ folderLabel }}
    </p>
    <b-form @submit.prevent="save">
      <b-form-group
        label="Data di scadenza"
        label-for="expiration-date-input"
      >
        <b-form-datepicker
          id="expiration-date-input"
          v-model="selectedDate"
          close-button
          :date-format-options="dateFormatOptions"
          :disabled="submitting"
          label-calendar="Calendario"
          label-close-button="Chiudi"
          label-current-month="Mese corrente"
          label-help="Usa le frecce per scegliere una data"
          label-next-month="Mese successivo"
          label-next-year="Anno successivo"
          label-no-date-selected="Nessuna data selezionata"
          label-prev-month="Mese precedente"
          label-prev-year="Anno precedente"
          label-selected="Data selezionata"
          label-today="Oggi"
          locale="it"
          :min="minDate"
          :start-weekday="1"
          :state="fieldError ? false : null"
        />
        <div
          v-if="fieldError"
          class="invalid-feedback d-block"
        >
          {{ INVALID_DATE_TEXT }}
        </div>
      </b-form-group>
    </b-form>
    <p class="text-muted small mb-0">
      {{ MODAL_HINT_TEXT }}
    </p>
    <template #modal-footer>
      <b-button
        :disabled="submitting"
        variant="outline-secondary"
        @click="closeModal"
      >
        Annulla
      </b-button>
      <b-button
        v-if="existingDate"
        :disabled="submitting"
        variant="outline-danger"
        @click="removeExpiration"
      >
        Rimuovi scadenza
      </b-button>
      <b-button
        :disabled="!canSave"
        variant="primary"
        @click="save"
      >
        Salva
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BButton, BForm, BFormDatepicker, BFormGroup, BModal,
} from 'bootstrap-vue'
import objectsRepository from '@/api/objectsRepository'
import useMyStore from '@/services/useMyStore'
import MyLoading from '@/layouts/components/MyLoading.vue'
import {
  INVALID_DATE_TEXT,
  MODAL_HINT_TEXT,
  MODAL_TITLE,
  REMOVE_CONFIRM,
  REMOVE_ERROR_OVERRIDES,
  REMOVE_SUCCESS_TEXT,
  SAVE_SUCCESS_TEXT,
  SET_ERROR_OVERRIDES,
  getFailureOutcome,
  getTomorrowDateString,
  isValidNewExpiration,
  normalizeDate,
  readExpirationUpdate,
} from '@/helpers/expiration-helpers'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'ExpirationModal',
  components: {
    BButton,
    BForm,
    BFormDatepicker,
    BFormGroup,
    BModal,
    MyLoading,
  },
  props: {
    files: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  setup() {
    const { currentObject } = useMyStore()
    return { currentObject }
  },
  data() {
    return {
      MODAL_TITLE,
      MODAL_HINT_TEXT,
      INVALID_DATE_TEXT,
      dateFormatOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },
      selectedDate: '',
      minDate: '',
      folderLabel: '',
      existingDate: null,
      fieldError: false,
      submitting: false,
      loadingShow: false,
    }
  },
  computed: {
    canSave() {
      return !this.submitting && isValidNewExpiration(this.selectedDate)
    },
  },
  methods: {
    onShow() {
      this.submitting = false
      this.loadingShow = false
      this.fieldError = false
      // Plain data reassigned on every open: a computed reading the clock would be cached forever.
      this.minDate = getTomorrowDateString()
      const row = this.files.find(f => f.id === this.currentObject)
      this.folderLabel = row ? row.label : ''
      this.existingDate = row ? normalizeDate(row.expiration_date) : null
      this.selectedDate = this.existingDate || ''
    },
    onHide(evt) {
      if (this.submitting) {
        evt.preventDefault()
      }
    },
    closeModal() {
      this.$bvModal.hide('expiration-modal')
    },
    showSuccess(text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Successo!',
          text,
          icon: 'CheckIcon',
          variant: 'success',
        },
      },
      {
        position: 'top-center',
      })
    },
    async save() {
      if (this.submitting) {
        return
      }
      const folderId = this.currentObject
      const date = this.selectedDate
      if (!isValidNewExpiration(date)) {
        this.fieldError = true
        return
      }
      this.submitting = true
      this.loadingShow = true
      this.fieldError = false
      try {
        const response = await objectsRepository.setExpiration(folderId, date)
        const update = readExpirationUpdate(response, folderId, date)
        if (update === null) {
          this.apiErrors.notifyApiError(response, SET_ERROR_OVERRIDES)
          return
        }
        this.$emit('expiration-updated', update)
        this.showSuccess(SAVE_SUCCESS_TEXT)
        // A hide while submitting is blocked, so the flag is cleared first.
        this.submitting = false
        this.$bvModal.hide('expiration-modal')
      } catch (e) {
        this.handleFailure(e, SET_ERROR_OVERRIDES)
      } finally {
        this.submitting = false
        this.loadingShow = false
      }
    },
    async removeExpiration() {
      if (this.submitting || !this.existingDate) {
        return
      }
      const folderId = this.currentObject
      // Raised before the dialog is awaited: a second click opens no second dialog.
      this.submitting = true
      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(REMOVE_CONFIRM.body, {
          title: REMOVE_CONFIRM.title,
          okTitle: REMOVE_CONFIRM.okTitle,
          cancelTitle: REMOVE_CONFIRM.cancelTitle,
          okVariant: 'danger',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        if (confirmed !== true) {
          return
        }
        this.loadingShow = true
        const response = await objectsRepository.removeExpiration(folderId)
        const update = readExpirationUpdate(response, folderId, null)
        if (update === null) {
          this.apiErrors.notifyApiError(response, REMOVE_ERROR_OVERRIDES)
          return
        }
        this.$emit('expiration-updated', update)
        this.showSuccess(REMOVE_SUCCESS_TEXT)
        // A hide while submitting is blocked, so the flag is cleared first.
        this.submitting = false
        this.$bvModal.hide('expiration-modal')
      } catch (e) {
        this.handleFailure(e, REMOVE_ERROR_OVERRIDES, false)
      } finally {
        this.submitting = false
        this.loadingShow = false
      }
    },
    handleFailure(error, overrides, allowInline = true) {
      // Only the fixed Italian override texts reach the user (Phase 1 D-02, D-09).
      this.apiErrors.notifyApiError(error, overrides)
      const fields = this.apiErrors.getFieldErrors(error)
      const outcome = getFailureOutcome(
        this.apiErrors.getErrorStatus(error),
        Array.isArray(fields.expiration_date),
      )
      if (outcome.inline && allowInline) {
        this.fieldError = true
      }
      if (outcome.stale) {
        this.$emit('stale')
      }
      if (outcome.close) {
        // A hide while submitting is blocked, so the flag is cleared first.
        this.submitting = false
        this.$bvModal.hide('expiration-modal')
      }
    },
  },
}
</script>
